import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@mondivago/supabase';
import { useSupplierProfile } from './useSupplierProfile';
import type { NotificationView as NotificationRow } from '@mondivago/types';
import { logger } from '@/shared/lib/logger';

export function useNotifications() {
  const { profile, agency } = useSupplierProfile();
  const supplierId = profile?.id ?? agency?.id ?? null;

  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchNotifications = useCallback(async () => {
    if (!supplierId) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: dbError } = await supabase
        .from('comm_notifications')
        .select('*')
        .eq('supplier_id', supplierId)
        .order('created_at', { ascending: false })
        .limit(50);

      if (dbError) throw dbError;
      setNotifications((data ?? []) as NotificationRow[]);
    } catch (err) {
      setError('Error loading notifications. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  useEffect(() => {
    void fetchNotifications();
  }, [fetchNotifications]);

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      const { error: dbError } = await supabase
        .from('comm_notifications')
        .update({ is_read: true })
        .eq('id', notificationId);
      if (dbError) throw dbError;

      setNotifications((prev) => prev.map((n) => (n.id === notificationId ? { ...n, is_read: true } : n)));
    } catch (err) {
      logger.error('Error marking notification as read:', err);
    }
  }, []);

  const markAllAsRead = useCallback(async () => {
    if (!supplierId) return;
    try {
      const { error: dbError } = await supabase
        .from('comm_notifications')
        .update({ is_read: true })
        .eq('supplier_id', supplierId)
        .eq('is_read', false);
      if (dbError) throw dbError;

      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (err) {
      logger.error('Error marking all notifications as read:', err);
    }
  }, [supplierId]);

  const unreadCount = notifications.filter((n) => !n.is_read).length;

  return { notifications, unreadCount, loading, error, refresh: fetchNotifications, markAsRead, markAllAsRead };
}
